import cx from 'clsx';
import { useRouter } from 'next/router';
import { Badge } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import {
  useShoppingCartCount,
  useShoppingCartPrice,
} from '~/domains/cart/queries';
import { useLinkComponent } from '~/hooks/mobileApp';

import StickyCartBarContent from './StickyCartBarContent';

import { PointLogo24x24Icon } from '~/assets/icons';
import LoadingSpinnerIcon from '~/assets/icons/scb_loading_spinner.svg';

interface StickyCartBarProps {
  placementName?: string;
  className?: string;
}

const StickyCartBar = ({ placementName, className }: StickyCartBarProps) => {
  const LinkComponent = useLinkComponent();
  const { count, isFetching: isFetchingCartCount } = useShoppingCartCount();
  const { data, isFetching: isFetchingCart } = useShoppingCartPrice('NONE');
  const router = useRouter();

  const onClick = () => {
    trackEvent('clicked SCB in 마켓', {
      placementName,
      path: decodeURI(router.asPath),
      query: router.query,
    });
  };

  if (!data || data?.totalAmount === 0) return <></>;

  const isAllFetching = !isFetchingCartCount && !isFetchingCart;

  return (
    <LinkComponent href="/cart" className="w-full">
      <button className="flex w-full flex-col" type="button" onClick={onClick}>
        <div
          className={cx([
            'flex items-center py-12 px-20 rounded-tr-16 rounded-tl-16 w-full bg-[#222b3ef2] pb-[calc(var(--ios-safe-bottom)+12px)]',
            className,
          ])}
        >
          <div className="flex flex-1 flex-col self-center">
            <StickyCartBarContent
              isFetching
              totalAmount={data.totalAmount}
              discountedTotalAmount={data.discountedTotalAmount}
            />
          </div>
          <div className="relative my-[1px] ml-12 flex h-40 w-80 shrink-0 items-center justify-center rounded-12 border-gray-300 bg-gray-000 text-gray-1400 typo-paragraph300-semibold border-1">
            {!isAllFetching ? (
              <div className="size-[30px]">
                <LoadingSpinnerIcon />
              </div>
            ) : (
              <>
                주문하기
                <Badge
                  type="number"
                  text={count}
                  className="absolute right-[1px] top-2 -translate-y-1/2 translate-x-1/2"
                />
              </>
            )}
          </div>
        </div>
        {!!data.expectedPoints && (
          <div className="flex items-center gap-4 bg-primary-air px-20 py-4">
            <PointLogo24x24Icon />
            <p className="typo-paragraph100-semibold">
              총{' '}
              <span className="text-accent">
                {data.expectedPoints.toLocaleString()}
              </span>{' '}
              포인트 적립 가능
            </p>
          </div>
        )}
      </button>
    </LinkComponent>
  );
};

export default StickyCartBar;
